import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { deleteDevice, getDevices, updateDevice } from '../api/endpoints';
import type { Device } from '../types';
import { formatRelative } from '../utils/dates';

const BRAND = '#00955F';
const BRAND_50 = '#E6F5EE';
const DANGER = '#D32020';
const DANGER_50 = '#FDEAEA';

function statusOf(lastSeenAt: string | null): { label: string; tone: 'ok' | 'danger' | 'neutral' } {
  if (!lastSeenAt) return { label: 'Sem sinal', tone: 'neutral' };
  const age = Date.now() - new Date(lastSeenAt).getTime();
  if (age < 10 * 60_000) return { label: 'Online', tone: 'ok' };
  return { label: 'Offline', tone: 'danger' };
}

const TONE_COLOR: Record<'ok' | 'danger' | 'neutral', string> = {
  ok: BRAND,
  danger: DANGER,
  neutral: '#9B998F',
};

const TONE_BG: Record<'ok' | 'danger' | 'neutral', string> = {
  ok: BRAND_50,
  danger: DANGER_50,
  neutral: '#EFEDE8',
};

function TruckIcon({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill={color}>
      <path d="M20 8h-3V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v9h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-2zM6 15.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9 1.96 2.5H17V6.5h2.5zM18 15.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
    </svg>
  );
}

export default function FrotaPage() {
  const [q, setQ] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: getDevices,
    refetchInterval: 10_000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, label, smsNumber, odometerKm }: { id: number; label: string; smsNumber: string | null; odometerKm: number }) =>
      updateDevice(id, { label, smsNumber, odometerKm }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success('Veículo atualizado.');
      setEditingId(null);
    },
    onError: () => toast.error('Falha ao atualizar veículo.'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success('Veículo removido da frota.');
    },
    onError: () => toast.error('Falha ao remover veículo.'),
  });

  function handleDelete(d: Device) {
    const name = d.label || d.imei.slice(-8);
    if (window.confirm(`Remover "${name}" da frota?\n\nTodo o histórico de posições deste rastreador será apagado. Esta ação não pode ser desfeita.`)) {
      deleteMutation.mutate(d.id);
    }
  }

  const filtered = devices.filter((d) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return [d.label, d.imei].some((v) => v?.toLowerCase().includes(needle));
  });

  const onlineCount = devices.filter((d) => d.lastSeenAt && Date.now() - new Date(d.lastSeenAt).getTime() < 10 * 60_000).length;

  return (
    <div className="flex flex-col h-full page-enter" style={{ background: '#F7F5F0' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E8E6E1', padding: '20px 24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif", color: '#0F0E0C', letterSpacing: '-0.02em', margin: 0, lineHeight: 1.2 }}>
              Frota
            </h1>
            <p style={{ fontSize: 12, color: '#9B998F', margin: '4px 0 0' }}>
              <span style={{ color: BRAND, fontWeight: 700 }}>{onlineCount}</span>
              {' '}online · {devices.length} dispositivo{devices.length !== 1 ? 's' : ''} no total
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Online progress bar */}
            <div style={{ width: 80, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <div style={{ width: '100%', height: 4, borderRadius: 9999, background: '#E8E6E1', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%', borderRadius: 9999, background: BRAND,
                    width: devices.length ? `${(onlineCount / devices.length) * 100}%` : '0%',
                    transition: 'width 0.6s ease',
                  }}
                />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#9B998F' }}>
                {devices.length ? Math.round((onlineCount / devices.length) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', maxWidth: 380 }}>
          <svg
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            width={15} height={15} viewBox="0 0 24 24" fill="none"
            stroke="#9B998F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou IMEI..."
            style={{
              width: '100%', height: 38, paddingLeft: 36, paddingRight: 12,
              border: '1px solid #E8E6E1', borderRadius: 10, fontSize: 13,
              background: '#F7F5F0', color: '#0F0E0C', outline: 'none',
              boxSizing: 'border-box', fontFamily: 'inherit',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND}22`; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#E8E6E1'; e.currentTarget.style.boxShadow = 'none'; }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        <div style={{
          background: '#FFFFFF', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(15,14,12,0.06), 0 0 0 1px rgba(15,14,12,0.06)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '28%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '22%' }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: '1px solid #E8E6E1' }}>
                {['Dispositivo', 'Última conexão', 'Odômetro', 'Status', ''].map((h) => (
                  <th key={h} style={{
                    padding: '14px 24px', textAlign: 'left',
                    fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.16em', color: '#9B998F',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const st = statusOf(d.lastSeenAt);
                const toneColor = TONE_COLOR[st.tone];
                const toneBg = TONE_BG[st.tone];
                return (
                  <DeviceRow
                    key={d.id}
                    device={d}
                    toneColor={toneColor}
                    toneBg={toneBg}
                    statusLabel={st.label}
                    isEditing={editingId === d.id}
                    onStartEdit={() => setEditingId(d.id)}
                    onCancelEdit={() => setEditingId(null)}
                    onSave={(label, smsNumber, odometerKm) => updateMutation.mutate({ id: d.id, label, smsNumber, odometerKm })}
                    isSaving={updateMutation.isPending && updateMutation.variables?.id === d.id}
                    onDelete={() => handleDelete(d)}
                    isDeleting={deleteMutation.isPending && deleteMutation.variables === d.id}
                  />
                );
              })}
            </tbody>
          </table>
          {!filtered.length && !isLoading && (
            <div style={{ padding: '48px 24px', textAlign: 'center', fontSize: 13, color: '#9B998F' }}>
              Nenhum dispositivo encontrado.
            </div>
          )}
          {isLoading && (
            <div style={{ padding: '48px 24px', textAlign: 'center', fontSize: 13, color: '#9B998F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg style={{ animation: 'spin 1s linear infinite' }} width={16} height={16} viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#E8E6E1" strokeWidth="4" />
                <path fill={BRAND} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Carregando...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface DeviceRowProps {
  device: Device;
  toneColor: string;
  toneBg: string;
  statusLabel: string;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (label: string, smsNumber: string | null, odometerKm: number) => void;
  isSaving: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}

function DeviceRow({
  device, toneColor, toneBg, statusLabel,
  isEditing, onStartEdit, onCancelEdit, onSave, isSaving,
  onDelete, isDeleting,
}: DeviceRowProps) {
  const [labelDraft, setLabelDraft] = useState(device.label);
  const [smsDraft, setSmsDraft] = useState(device.smsNumber ?? '');
  const [odometerDraft, setOdometerDraft] = useState(String(Math.round(device.odometerKm)));

  if (isEditing) {
    return (
      <tr style={{ borderBottom: '1px solid #E8E6E1', background: '#FAFAF8' }}>
        <td colSpan={2} style={{ padding: '10px 24px' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              placeholder="Nome do veículo"
              autoFocus
              style={{
                flex: 1, height: 34, padding: '0 10px', border: '1px solid #E8E6E1',
                borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit',
              }}
            />
            <input
              value={smsDraft}
              onChange={(e) => setSmsDraft(e.target.value)}
              placeholder="Número SMS (opcional)"
              style={{
                width: 170, height: 34, padding: '0 10px', border: '1px solid #E8E6E1',
                borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>
        </td>
        <td style={{ padding: '10px 24px' }}>
          <input
            type="number"
            min={0}
            value={odometerDraft}
            onChange={(e) => setOdometerDraft(e.target.value)}
            placeholder="Odômetro (km)"
            title="Leitura atual do painel do veículo — o sistema soma o percorrido por GPS a partir daqui"
            style={{
              width: '100%', height: 34, padding: '0 10px', border: '1px solid #E8E6E1',
              borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </td>
        <td colSpan={2} style={{ padding: '10px 24px', textAlign: 'right' }}>
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              onClick={() => {
                const parsed = Number(odometerDraft);
                const odometerKm = odometerDraft.trim() === '' || isNaN(parsed) || parsed < 0
                  ? device.odometerKm
                  : parsed;
                onSave(labelDraft.trim() || device.imei.slice(-8), smsDraft.trim() || null, odometerKm);
              }}
              disabled={isSaving}
              style={{
                padding: '7px 14px', background: BRAND, color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 12.5, fontWeight: 700, opacity: isSaving ? 0.6 : 1,
              }}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              onClick={onCancelEdit}
              disabled={isSaving}
              style={{
                padding: '7px 14px', background: '#EFEDE8', color: '#6B6860', border: 'none',
                borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              }}
            >
              Cancelar
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr
      style={{ borderBottom: '1px solid #E8E6E1', cursor: 'default', position: 'relative' }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = '#FAFAF8'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
    >
      <td style={{ padding: '14px 24px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Accent left bar */}
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
            background: toneColor, borderRadius: '0 2px 2px 0',
          }} />
          {/* Truck avatar */}
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: toneBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid #E8E6E1',
          }}>
            <TruckIcon color={toneColor} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 15, fontWeight: 700,
              fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif",
              color: '#0F0E0C', lineHeight: 1, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {device.label || device.imei.slice(-8)}
            </div>
            <div style={{ fontSize: 11, color: '#9B998F', marginTop: 4, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
              {device.imei}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding: '14px 24px', fontSize: 12.5, color: '#6B6860' }}>
        {device.lastSeenAt
          ? formatRelative(device.lastSeenAt)
          : <span style={{ color: '#C4C1BA' }}>—</span>}
      </td>
      <td style={{ padding: '14px 24px', fontSize: 12.5, color: '#6B6860' }}>
        {device.odometerKm.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km
      </td>
      <td style={{ padding: '14px 24px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 9999,
          fontSize: 11.5, fontWeight: 700,
          background: toneBg, color: toneColor,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: toneColor, flexShrink: 0 }} />
          {statusLabel}
        </span>
      </td>
      <td style={{ padding: '14px 24px', textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <button
            onClick={onStartEdit}
            title="Editar veículo"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: '#EFEDE8', color: '#6B6860',
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
            }}
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Editar
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            title="Remover veículo da frota"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: DANGER_50, color: DANGER,
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
              opacity: isDeleting ? 0.5 : 1,
            }}
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
            </svg>
            {isDeleting ? 'Removendo...' : 'Remover'}
          </button>
        </div>
      </td>
    </tr>
  );
}
